/* ============================================================
   LKWood · Cart Upsell — Anavrin-inspired
   Target theme : Shopify Dawn 8.0.0
   Store        : lkwoodglobal.com
   -----------------------------------------------------------
   Responsibilities:
     1. Reservation timer countdown (Anavrin style, mm:ss)
     2. Multi-tier discount progress bar + per-tier check icons
     3. Free-shipping bar refresh on every cart change
     4. Hide upsell cards for products already in the cart
     5. Add-to-cart via POST /cart/add.js  (Section Rendering API)
     6. Broadcast Dawn's PUB_SUB_EVENTS.cartUpdate

   SAFETY / ISOLATION:
     - Zero override of Dawn classes / prototypes / fetch.
     - Uses Dawn's public API: cartDrawer.renderContents(),
       cartDrawer.open(), window.subscribe/publish('cart-update').
     - Fails silently if Dawn's cart-drawer is missing.
   ============================================================ */
(function () {
  'use strict';

  if (window.customElements && customElements.get('lkwood-cart-upsell')) return;

  var CART_URL = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart';
  var ADD_URL  = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart/add.js';
  var CART_JS  = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'cart.js';
  var DAWN_CART_UPDATE_EVENT = 'cart-update';

  function pad2(n) { return (n < 10 ? '0' : '') + n; }

  function formatMoneyFallback(cents) {
    var val = (Math.max(0, cents) / 100).toFixed(2);
    return '$' + val;
  }
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.Shopify.money_format) {
      try { return window.Shopify.formatMoney(cents, window.Shopify.money_format); }
      catch (e) { return formatMoneyFallback(cents); }
    }
    return formatMoneyFallback(cents);
  }

  class LKWoodCartUpsell extends HTMLElement {
    constructor() {
      super();
      this._cartIds = new Set();
      this._threshold = parseInt(this.getAttribute('data-threshold'), 10) || 0;
      this._tiers = this._parseTiers(this.getAttribute('data-tiers') || '');
      this._boundOnClick = this._onClick.bind(this);
      this._pubsubUnsub = null;
      this._timerInterval = null;
    }

    connectedCallback() {
      // Read cart product IDs (rendered by Liquid)
      var idsScript = this.querySelector('script[data-lkwood-cart-ids]');
      if (idsScript) {
        try {
          var ids = JSON.parse(idsScript.textContent || '[]');
          if (Array.isArray(ids)) this._cartIds = new Set(ids.map(Number));
        } catch (e) { /* ignore */ }
      }
      this._hideCardsInCart();

      // Click delegation
      this.addEventListener('click', this._boundOnClick);

      // Reservation timer countdown
      this._startTimer();

      // Subscribe to Dawn 8.0.0 cart update
      if (typeof window.subscribe === 'function') {
        try {
          this._pubsubUnsub = window.subscribe(DAWN_CART_UPDATE_EVENT, this._onDawnCartUpdate.bind(this));
        } catch (e) { /* ignore */ }
      }
      document.addEventListener('lkwood:cart-refreshed', this._onDawnCartUpdate.bind(this));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._boundOnClick);
      if (this._timerInterval) { clearInterval(this._timerInterval); this._timerInterval = null; }
      if (typeof this._pubsubUnsub === 'function') {
        try { this._pubsubUnsub(); } catch (e) { /* ignore */ }
      }
    }

    /* ---------------- Tiers parsing ---------------- */
    _parseTiers(str) {
      if (!str) return [];
      var out = [];
      str.split(',').forEach(function (part) {
        var seg = part.trim().split(':');
        if (seg.length < 2) return;
        var dollars = parseFloat(seg[0]);
        var label = seg.slice(1).join(':').trim();
        if (isNaN(dollars) || !label) return;
        out.push({ cents: Math.round(dollars * 100), label: label });
      });
      return out.sort(function (a, b) { return a.cents - b.cents; });
    }

    /* ---------------- Timer ---------------- */
    _startTimer() {
      var el = this.querySelector('[data-lkwood-timer]');
      if (!el) return;
      var seconds = parseInt(el.getAttribute('data-seconds'), 10) || 0;
      if (seconds <= 0) return;

      // Persist across refreshes within tab using sessionStorage
      var key = 'lkwood-timer-expires';
      var now = Date.now();
      var expires;
      try {
        expires = parseInt(sessionStorage.getItem(key), 10);
      } catch (e) { expires = 0; }
      if (!expires || isNaN(expires) || expires < now) {
        expires = now + seconds * 1000;
        try { sessionStorage.setItem(key, String(expires)); } catch (e) {}
      }

      var valueEl = el.querySelector('[data-lkwood-timer-value]');
      var self = this;
      var tick = function () {
        var remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
        var m = Math.floor(remaining / 60);
        var s = remaining % 60;
        if (valueEl) valueEl.textContent = pad2(m) + ':' + pad2(s);
        if (remaining <= 0) {
          clearInterval(self._timerInterval);
          self._timerInterval = null;
          // Reset for next cycle so it re-arms next visit
          try { sessionStorage.removeItem(key); } catch (e) {}
          el.classList.add('is-expired');
        }
      };
      tick();
      this._timerInterval = setInterval(tick, 1000);
    }

    /* ---------------- Click delegation ---------------- */
    _onClick(evt) {
      var btn = evt.target.closest('[data-lkwood-add]');
      if (!btn) return;
      if (btn.tagName === 'A') return; // link fallback for multi-variant
      if (btn.disabled || btn.classList.contains('is-loading')) return;
      evt.preventDefault();
      this._addToCart(btn);
    }

    /* ---------------- Add To Cart ---------------- */
    _addToCart(btn) {
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var card = btn.closest('[data-lkwood-upsell-card]');
      btn.classList.add('is-loading');
      btn.disabled = true;

      var sections = ['cart-drawer', 'cart-icon-bubble'];
      if (document.body.classList.contains('template-cart') ||
          location.pathname.replace(/\/$/, '').endsWith('/cart')) {
        sections.push('main-cart-items', 'main-cart-footer', 'cart-live-region-text');
      }

      var body = {
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
        sections: sections.join(','),
        sections_url: location.pathname
      };

      var self = this;
      fetch(ADD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw data;
            return data;
          });
        })
        .then(function (data) {
          self._afterAdd(btn, card);
          self._refreshDawn(data);
          self._openDrawer();
        })
        .catch(function (err) {
          console.warn('[lkwood-cart-upsell] Add failed:', err);
          self._showError(btn);
        });
    }

    _afterAdd(btn, card) {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.disabled = false;
      var label = btn.querySelector('.lkwood-upsell-card__add-label');
      if (label) label.textContent = 'Added ✓';
      if (card) {
        card.classList.add('is-added');
        setTimeout(function () { card.classList.add('is-in-cart'); }, 700);
      }
    }

    _showError(btn) {
      btn.classList.remove('is-loading');
      btn.classList.add('is-error');
      btn.disabled = false;
      var label = btn.querySelector('.lkwood-upsell-card__add-label');
      var original = label ? label.textContent : '→ Add';
      if (label) label.textContent = 'Try again';
      setTimeout(function () {
        btn.classList.remove('is-error');
        if (label) label.textContent = original || '→ Add';
      }, 1600);
    }

    /* ---------------- Dawn integration ---------------- */
    _refreshDawn(data) {
      var self = this;
      if (data && data.sections) {
        self._applySections(data);
        self._publishDawnCartUpdate();
        return;
      }
      // Fallback fetch
      fetch(CART_JS, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          self._updateFromCart(cart);
          self._publishDawnCartUpdate();
        })
        .catch(function () {});
    }

    _applySections(parsedState) {
      var sections = parsedState.sections || {};
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        try {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(parsedState);
        } catch (e) {
          this._manualSectionReplace(sections);
        }
      } else {
        this._manualSectionReplace(sections);
      }

      var self = this;
      fetch(CART_JS, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self._updateFromCart(cart); })
        .catch(function () {});
    }

    _manualSectionReplace(sections) {
      Object.keys(sections).forEach(function (key) {
        var html = sections[key];
        if (!html) return;
        var mount;
        if (key === 'cart-icon-bubble')      mount = document.getElementById('cart-icon-bubble');
        else if (key === 'cart-drawer')       mount = document.getElementById('CartDrawer');
        else if (key === 'main-cart-items')   mount = document.querySelector('.js-contents');
        else if (key === 'main-cart-footer')  mount = document.getElementById('main-cart-footer');
        if (!mount) return;
        try {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var source;
          if (key === 'cart-icon-bubble')     source = doc.querySelector('.shopify-section');
          else if (key === 'cart-drawer')     source = doc.getElementById('CartDrawer');
          else                                source = doc.querySelector('.js-contents') || doc.getElementById(mount.id);
          if (source) mount.innerHTML = source.innerHTML;
        } catch (e) {}
      });
    }

    _openDrawer() {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function' && !cartDrawer.classList.contains('active')) {
        try { cartDrawer.open(); } catch (e) {}
      }
    }

    _publishDawnCartUpdate() {
      if (typeof window.publish === 'function') {
        try { window.publish(DAWN_CART_UPDATE_EVENT, { source: 'lkwood-cart-upsell' }); }
        catch (e) {}
      }
    }

    _onDawnCartUpdate(event) {
      if (event && event.source === 'lkwood-cart-upsell') return;
      var self = this;
      fetch(CART_JS, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self._updateFromCart(cart); })
        .catch(function () {});
    }

    /* ---------------- Cart state -> UI ---------------- */
    _updateFromCart(cart) {
      if (!cart) return;
      this._cartIds = new Set((cart.items || []).map(function (i) { return Number(i.product_id); }));
      this._hideCardsInCart();
      this._updateFreeship(cart.total_price || 0);
    }

    _hideCardsInCart() {
      var cards = this.querySelectorAll('[data-lkwood-upsell-card]');
      var self = this;
      cards.forEach(function (card) {
        var pid = Number(card.getAttribute('data-product-id'));
        if (self._cartIds.has(pid)) card.classList.add('is-in-cart');
        else                        card.classList.remove('is-in-cart');
      });
    }

    _updateFreeship(totalCents) {
      var bar = this.querySelector('[data-lkwood-freeship]');
      if (!bar) return;
      var threshold = parseInt(bar.getAttribute('data-threshold'), 10) || this._threshold;

      // Compute the maximum reference point for the progress bar.
      // If tiers exist, use the highest tier; otherwise use the free-shipping threshold.
      var max = threshold;
      if (this._tiers.length > 0) {
        max = Math.max(threshold, this._tiers[this._tiers.length - 1].cents);
      }

      var pct = 0;
      if (max > 0) pct = Math.min(100, Math.round((totalCents / max) * 100));
      var remaining = Math.max(0, threshold - totalCents);
      var unlocked = threshold > 0 && totalCents >= threshold;

      var fill = bar.querySelector('[data-lkwood-freeship-fill]');
      if (fill) {
        fill.style.width = pct + '%';
        fill.classList.toggle('is-unlocked', unlocked);
      }
      var track = bar.querySelector('.lkwood-freeship__track');
      if (track) track.setAttribute('aria-valuenow', String(pct));

      var textEl = bar.querySelector('[data-lkwood-freeship-text]');
      if (textEl) {
        if (unlocked) {
          textEl.innerHTML =
            '<span class="lkwood-freeship__icon" aria-hidden="true">🎉</span>' +
            '<span class="lkwood-freeship__unlock">Congrats! You\'ve unlocked <strong>FREE SHIPPING</strong></span>';
        } else {
          textEl.innerHTML =
            '<span class="lkwood-freeship__icon" aria-hidden="true">🚚</span>' +
            '<span>You\'re <strong data-lkwood-freeship-remaining>' + formatMoney(remaining) +
            '</strong> away from <strong>FREE SHIPPING</strong></span>';
        }
      }

      // Update tiers: mark achieved for each milestone <= totalCents.
      var tierEls = bar.querySelectorAll('.lkwood-tier');
      if (tierEls.length > 0) {
        tierEls.forEach(function (t) {
          var c = parseInt(t.getAttribute('data-tier-cents'), 10) || 0;
          var achieved = totalCents >= c && c > 0;
          t.classList.toggle('is-achieved', achieved);
          // active = next to be unlocked
          t.classList.remove('is-active');
          if (!achieved) {
            var prev = t.previousElementSibling;
            if (!prev || prev.classList.contains('is-achieved') || totalCents === 0) {
              // Only mark first not-achieved as active
              var allTiers = Array.prototype.slice.call(tierEls);
              var firstNotAchieved = allTiers.find(function (x) {
                var cc = parseInt(x.getAttribute('data-tier-cents'), 10) || 0;
                return totalCents < cc;
              });
              if (firstNotAchieved === t) t.classList.add('is-active');
            }
          }
          // Swap dot icon (SVG) between tag and check
          var dot = t.querySelector('.lkwood-tier__dot');
          if (dot) {
            if (achieved) {
              dot.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
            } else {
              dot.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>';
            }
          }
        });
      }
    }
  }

  customElements.define('lkwood-cart-upsell', LKWoodCartUpsell);
})();
